import { Bus, ChevronRight, Footprints, MapPin, Plane, Route, Train } from "lucide-react";
import { useRef } from "react";

import { MarkdownContent } from "../../../shared/ui";
import type {
  PublicItinerary,
  PublicItineraryNode,
  PublicItinerarySegment,
  PublicItineraryTrack,
  PublicTimeSlot,
} from "../types";
import { normalizeServiceStandardPreviewRecords, requiresLongBusReview, summarizeDayEndpoints } from "../service";
import { formatTransportDuration, formatTransportTimeRange } from "../service/itineraryTransportTime";
import { ITINERARY_SERVICE_STANDARD_TYPES } from "../serviceStandardTypes";
import { ItineraryMarkdown, ItineraryMarkdownProvider } from "./ItineraryMarkdown";
import { ItineraryCustomerHeader, ItineraryHotelInfo } from "./ItineraryCustomerHeader";
import { canonicalPublicSegments, customerTransitSegmentsForDate, customerVisibleInterDaySegments, publicSegmentMatchesNodes } from "./publicItineraryPath";

type Props = {
  itinerary: PublicItinerary;
  onShowDetail?: (dayNumber: number, focusNav: boolean) => void;
};

const slots: PublicTimeSlot[] = ["morning", "afternoon", "evening", "unscheduled"];
const slotLabels: Record<PublicTimeSlot, string> = { morning: "上午", afternoon: "下午", evening: "晚上", unscheduled: "未分时" };
const nodeTypeLabels: Record<PublicItineraryNode["nodeType"], string> = {
  point: "考察点",
  railway_station: "火车站",
  airport: "机场",
  stay_area: "住宿",
  organization_site: "学校",
  custom: "考察点",
};
const transportLabels: Record<PublicItinerarySegment["transportMode"], string> = {
  rail: "火车",
  air: "飞机",
  bus: "大巴",
  walk: "步行",
  other: "其他交通",
};
const transportIcons = {
  rail: Train,
  air: Plane,
  bus: Bus,
  walk: Footprints,
  other: Route,
} as const;

/** 交通图标使用自然中文名称，避免“其他交通”重复拼接“交通”。 */
export function transportIconLabel(mode: PublicItinerarySegment["transportMode"]) {
  return mode === "other" ? transportLabels[mode] : `${transportLabels[mode]}交通`;
}

/** 客户简版行程，以按日分组的报告布局连续展示点位与交通。 */
export function ItineraryClientPreview({ itinerary, onShowDetail }: Props) {
  const dayNumbers = new Set(itinerary.days.map((day) => day.dayNumber));
  const trailingHotels = itinerary.hotels.filter((hotel) =>
    !hotel.dayNumbers.some((dayNumber) => dayNumbers.has(dayNumber)),
  );
  return (
    <ItineraryMarkdownProvider itinerary={itinerary}>
    <article className="itinerary-client-sheet itinerary-client-preview">
      <ItineraryCustomerHeader itinerary={itinerary} />
      <div className="itinerary-preview-days" aria-label="客户简版行程">
        {itinerary.days.length ? itinerary.days.map((day) => (
          <PreviewDay
            key={day.dayNumber}
            day={day}
            hotels={itinerary.hotels.filter((hotel) => hotel.dayNumbers.includes(day.dayNumber))}
            transitSegments={customerTransitSegmentsForDate(itinerary.days, day.dayDate)}
            onShowDetail={onShowDetail}
          />
        )) : <p className="itinerary-output-empty">行程日期待补充</p>}
      </div>
      <ItineraryHotelInfo hotels={trailingHotels} ariaLabel="未关联天数酒店信息" />
      <ItineraryServiceAppendix itinerary={itinerary} />
    </article>
    </ItineraryMarkdownProvider>
  );
}

/** 渲染简版单日的首尾锚点与时段安排。 */
function PreviewDay({
  day,
  hotels,
  transitSegments,
  onShowDetail,
}: {
  day: PublicItinerary["days"][number];
  hotels: PublicItinerary["hotels"];
  transitSegments: PublicItinerarySegment[];
  onShowDetail?: (dayNumber: number, focusNav: boolean) => void;
}) {
  const keyboardActivationRef = useRef(false);
  const orderedNodes = [...day.nodes].sort(comparePreviewNodes);
  const primaryTrackNodes = day.parallelTracks?.[0]?.nodes ?? [];
  const anchorNodes = primaryTrackNodes.length ? primaryTrackNodes : orderedNodes;
  const endpoints = summarizeDayEndpoints(anchorNodes);
  const first = endpoints?.first;
  const last = endpoints?.last;
  const focusTitles = [...new Set(anchorNodes.slice(1, -1)
    .filter((node) => node.nodeType === "point" || node.nodeType === "custom")
    .map((node) => node.title.trim())
    .filter(Boolean))];
  const visibleFocusTitles = focusTitles.slice(0, 2);
  const hasCondensedFocusTitles = visibleFocusTitles.length < focusTitles.length;
  const interDaySegments = customerVisibleInterDaySegments(day.segments);
  const hasTrackContent = (day.parallelTracks ?? []).some((track) =>
    track.nodes.length > 0 || canonicalPublicSegments(track.nodes, track.segments).length > 0,
  );
  const hasSummary = Boolean(day.summary || day.morningSummary || day.afternoonSummary || day.eveningSummary);
  const isEmpty = !orderedNodes.length
    && !canonicalPublicSegments(day.nodes, day.segments).length
    && !interDaySegments.length
    && !transitSegments.length
    && !hasTrackContent
    && !hasSummary;
  return (
    <section className={`itinerary-preview-day${isEmpty ? " is-empty" : ""}${transitSegments.length ? " is-transit" : ""}`} aria-label={`D${day.dayNumber} 客户简版`}>
      <header className="itinerary-preview-day-head">
        <div className="itinerary-preview-day-identity">
          <span className={`itinerary-preview-day-badge${isEmpty ? " is-muted" : ""}`}>D{day.dayNumber}</span>
          <span className="itinerary-preview-day-date">{day.dayDate || "日期待定"}</span>
        </div>
        <div className="itinerary-preview-day-title">
          <h2>{customerDayTitle(day)}</h2>
          {endpoints?.isSameLocation || first && last || transitSegments.length || !hasSummary ? (
            <p className="itinerary-preview-anchor-path">
              {endpoints?.isSameLocation ? <strong>{endpoints.label}</strong> : first && last ? <>
                <span className="itinerary-preview-anchor-part"><small>{nodeTypeLabels[first.nodeType]}起点</small><strong>{first.title}</strong></span>
                {focusTitles.length ? <span className="itinerary-preview-anchor-part"><i aria-hidden="true">→</i><small>途经</small><strong title={hasCondensedFocusTitles ? focusTitles.join("、") : undefined}>{visibleFocusTitles.join("、")}</strong>{hasCondensedFocusTitles ? <small className="itinerary-preview-anchor-more">等 {focusTitles.length} 个点</small> : null}</span> : null}
                <span className="itinerary-preview-anchor-part"><i aria-hidden="true">→</i><small>{nodeTypeLabels[last.nodeType]}终点</small><strong>{last.title}</strong></span>
              </> : transitSegments.length ? <TransitSummary segments={transitSegments} /> : <span>首尾锚点待确认</span>}
            </p>
          ) : null}
        </div>
        {onShowDetail ? <button
          type="button"
          className="itinerary-preview-detail-link"
          aria-label={`查看第${day.dayNumber}天详情`}
          title={`查看第${day.dayNumber}天详情`}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") keyboardActivationRef.current = true;
          }}
          onBlur={() => { keyboardActivationRef.current = false; }}
          onClick={(event) => {
            const focusNav = keyboardActivationRef.current;
            keyboardActivationRef.current = false;
            if (!focusNav) {
              event.currentTarget.blur();
              const activeElement = document.activeElement;
              if (activeElement instanceof HTMLElement && activeElement.closest(".itinerary-output-toolbar, .itinerary-public-toolbar")) activeElement.blur();
            }
            onShowDetail(day.dayNumber, focusNav);
          }}
        ><ChevronRight size={18} aria-hidden="true" /></button> : null}
      </header>
      <div className="itinerary-preview-day-body">
        {slots.map((slot) => <PreviewSlot key={slot} day={day} slot={slot} />)}
        {interDaySegments.length ? <section className="itinerary-preview-slot is-interday" role="region" aria-label="跨日衔接简版">
          <strong className="itinerary-preview-slot-label">跨日</strong>
          <div className="itinerary-preview-slot-content"><PreviewPath items={interDaySegments.map((segment) => ({ kind: "segment", segment }))} label="跨日衔接连续路径" /></div>
        </section> : null}
      </div>
      <ItineraryHotelInfo hotels={hotels} ariaLabel={`D${day.dayNumber} 酒店信息`} />
    </section>
  );
}

/** 用一行真实事实解释没有点位的跨夜在途日期。 */
function TransitSummary({ segments }: { segments: PublicItinerarySegment[] }) {
  return <span className="itinerary-preview-transit-summary">
    <strong>全天在途</strong>
    {segments.map((segment, index) => {
      const routeCode = segment.primaryTransport?.routeCode?.trim() || segment.routeCode?.trim();
      const route = segment.fromTitle && segment.toTitle ? `${segment.fromTitle} → ${segment.toTitle}` : segment.name;
      return <small key={`${segment.name}-${index}`}>{[
        routeCode,
        segment.hasStayEndpoint ? null : transportLabels[segment.transportMode],
        route,
      ].filter(Boolean).join(" · ")}</small>;
    })}
  </span>;
}

/** 用中文日次替换系统生成的通用英文标题，同时保留人工填写的当天主题。 */
export function customerDayTitle(day: PublicItinerary["days"][number]) {
  const title = day.dayTitle?.trim();
  const dayLabel = `第${day.dayNumber}天`;
  if (!title || /^Day\s+\d+$/i.test(title) || title === dayLabel) return dayLabel;
  return `${dayLabel} ${title}`;
}

/** 渲染单个时段，并把专业交通留在所属轨道内。 */
function PreviewSlot({ day, slot }: { day: PublicItinerary["days"][number]; slot: PublicTimeSlot }) {
  const tracks = (day.parallelTracks ?? []).map((track) => ({
    track,
    items: previewPathItems(track.nodes, track.segments, slot),
  })).filter(({ items }) => items.length);
  const items = previewPathItems(day.nodes, day.segments, slot);
  const commonSegments = tracks.length
    ? items.filter((item): item is Extract<PreviewPathItem, { kind: "segment" }> => item.kind === "segment")
    : [];
  if (!tracks.length && !items.length) return null;
  return (
    <section className="itinerary-preview-slot" aria-label={`${slotLabels[slot]}行程简版`}>
      <strong className="itinerary-preview-slot-label">{day.isFullDay && slot === "morning" ? "全天" : slotLabels[slot]}</strong>
      <div className="itinerary-preview-slot-content">
        {tracks.length ? <>
          {tracks.map(({ track, items: trackItems }) => (
            <PreviewTrack key={track.title} track={track} items={trackItems} slot={slot} />
          ))}
          {commonSegments.length ? (
            <section className="itinerary-preview-track is-common" aria-label="公共交通简版路径">
              <strong>公共交通</strong>
              <PreviewPath items={commonSegments} label="公共交通" />
            </section>
          ) : null}
        </> : <PreviewPath items={items} label={`${slotLabels[slot]}连续路径`} />}
      </div>
    </section>
  );
}

/** 渲染简版专业轨道。 */
function PreviewTrack({ track, items, slot }: { track: PublicItineraryTrack; items: PreviewPathItem[]; slot: PublicTimeSlot }) {
  const label = `${track.title}${slotLabels[slot]}`;
  return <section className="itinerary-preview-track" aria-label={`${label}简版路径`}><strong>{track.title}</strong><PreviewPath items={items} label={`${label}连续路径`} /></section>;
}

/** 渲染考察点路径与紧随其后的交通事实。 */
function PreviewPath({ items, label }: { items: PreviewPathItem[]; label: string }) {
  return <ol className="itinerary-preview-path" aria-label={label}>
    {items.map((item, index) => item.kind === "node" ? (
      <li className="itinerary-preview-path-item is-point" aria-label={`考察点 ${item.node.title}`} key={`point-${item.node.title}-${index}`}>
        <span className="itinerary-preview-path-icon is-point" role="img" aria-label="考察点"><MapPin size={14} /></span>
        <strong>{item.node.title}</strong>
      </li>
    ) : <PreviewRouteItem item={item} key={`route-${item.segment.name}-${index}`} />)}
  </ol>;
}

/** 使用交通方式图标渲染简版路线项。 */
function PreviewRouteItem({ item }: { item: Extract<PreviewPathItem, { kind: "segment" }> }) {
  const TransportIcon = transportIcons[item.segment.transportMode];
  const transportLabel = item.segment.hasStayEndpoint ? "" : transportLabels[item.segment.transportMode];
  const iconLabel = transportIconLabel(item.segment.transportMode);
  const endpointLabel = item.segment.fromTitle && item.segment.toTitle
    ? `${item.segment.fromTitle} - ${item.segment.toTitle}`
    : item.segment.name;
  return (
    <li className="itinerary-preview-path-item is-route" aria-label={`交通 ${endpointLabel}`}>
      <span className={`itinerary-preview-path-icon is-${item.segment.transportMode}`} role="img" aria-label={iconLabel} title={iconLabel}><TransportIcon size={14} /></span>
      <p>{[endpointLabel, transportLabel].filter(Boolean).join(" · ")}</p>
    </li>
  );
}

type PreviewPathItem = { kind: "node"; node: PublicItineraryNode } | { kind: "segment"; segment: PublicItinerarySegment };

/** 按编辑器时段与序号构建交通归入到达点时段的连续简版路径。 */
function previewPathItems(nodes: PublicItineraryNode[], segments: PublicItinerarySegment[], slot: PublicTimeSlot): PreviewPathItem[] {
  const ordered = [...nodes].sort(comparePreviewNodes);
  const canonicalSegments = canonicalPublicSegments(nodes, segments);
  const used = new Set<PublicItinerarySegment>();
  const items: PreviewPathItem[] = [];
  ordered.forEach((node, index) => {
    if (node.timeSlot === slot && (node.nodeType === "point" || node.nodeType === "custom")) items.push({ kind: "node", node });
    const next = ordered[index + 1];
    if (!next) return;
    const segment = canonicalSegments.find((candidate) =>
      !used.has(candidate) && publicSegmentMatchesNodes(candidate, node, next),
    );
    if (!segment) return;
    used.add(segment);
    if (next.timeSlot === slot) items.push({ kind: "segment", segment });
  });
  return items;
}

/** 还原编辑器的上午、下午、晚上及各时段内顺序。 */
function comparePreviewNodes(left: PublicItineraryNode, right: PublicItineraryNode) {
  return slots.indexOf(left.timeSlot) - slots.indexOf(right.timeSlot) || left.sortOrder - right.sortOrder;
}

/** 组合客户可见的路线事实，不为缺失时间补造事实。 */
export function routeText(segment: PublicItinerarySegment): string {
  const timeRange = formatTransportTimeRange(segment.departureAt, segment.arrivalAt);
  const routeLabel = segment.primaryTransport
    ? segment.primaryTransport.routeCode?.trim() || segment.primaryTransport.label?.trim()
    : segment.routeCode?.trim();
  return [
    segment.fromTitle && segment.toTitle ? `${segment.fromTitle} - ${segment.toTitle}` : segment.name,
    segment.hasStayEndpoint ? "" : transportLabels[segment.transportMode],
    routeLabel,
    segment.primaryTransport?.seatClass?.trim(),
    segment.primaryTransport?.cabinClass?.trim(),
    segment.hasStayEndpoint ? "" : timeRange,
    !segment.hasStayEndpoint && segment.distanceKm?.trim() ? `${segment.distanceKm} 公里` : "",
    segment.hasStayEndpoint ? "" : formatTransportDuration(segment.durationMinutes),
    !segment.hasStayEndpoint && requiresLongBusReview(segment) ? "超长大巴，待核对" : "",
    segment.isPending ? "待确认" : "",
  ].filter(Boolean).join(" · ");
}

/** 客户输出共用的服务标准与注意事项。 */
export function ItineraryServiceAppendix({ itinerary }: { itinerary: PublicItinerary }) {
  const customerNotices = itinerary.customerNotices?.trim();
  const serviceStandards = ITINERARY_SERVICE_STANDARD_TYPES.map(([key, label]) => ({
    key,
    label,
    records: normalizeServiceStandardPreviewRecords(itinerary.customerServiceStandards?.[key] ?? []),
  })).filter(({ records }) => records.length);

  return (
    <footer className="itinerary-output-appendix">
      <section className="itinerary-output-service-standard">
        <h2>服务标准</h2>
        <div className={`itinerary-output-services${serviceStandards.length ? "" : " is-empty"}`}>
          {serviceStandards.length ? serviceStandards.map(({ key, label, records }, standardIndex) => {
            const RecordsList = records.length > 1 ? "ol" : "ul";
            return (
              <section className="itinerary-output-service-row" key={key}>
                <strong>{standardIndex + 1}、{label.replace(/标准$/, "")}</strong>
                <RecordsList>{records.map((item, index) => <li key={`${key}-${index}`}><MarkdownContent content={item} /></li>)}</RecordsList>
              </section>
            );
          }) : <p>暂无服务标准</p>}
        </div>
      </section>
      <section className={`itinerary-output-notices${customerNotices ? "" : " is-empty"}`}><h2>注意事项</h2><ItineraryMarkdown content={customerNotices || "暂无特别注意事项"} /></section>
    </footer>
  );
}
